import { ManipulateType } from 'dayjs';
import _ from 'lodash';
import {
	DateFieldConfiguration,
	DateOffsetConfiguration,
	FieldConfiguration,
	GroupFieldConfiguration,
	NumberFieldConfiguration,
	PerilConfigurationResponse,
	PolicyConfiguration,
	StringFieldConfiguration,
} from '../../../api/ConfigApi';
import { dayjs } from '../../../date-util';
import { ExposureConfiguration } from '../../../types/endorsements';
import {
	Exposure,
	ExposureCreateRequest,
	ExposureUpdateRequest,
	FieldGroupsByLocatorMap,
	FieldValues,
	FieldValuesMap,
	Peril,
	PerilCreateRequest,
	Policy2,
	PolicyCharacteristics,
	PolicyUpdateRequest,
} from '../../../types/policy';
import {
	ValidationFailureResult,
	ValidationResult,
	ValidationSuccessResult,
} from '../../../types/validations';
import {
	getExposureCharacteristics,
	getPerilCoverage,
	getPolicyCharacteristics,
	isPerilActive,
} from '../../../util/util';
import {
	getAmendedFieldData,
	getCharacteristicsFromCreateRequest,
} from '../Endorsements/util';
import { isFieldAvailableForDisplay } from './util';

export type Validation<T> = {
	[K in keyof T]?: ValidationResult;
};

interface FieldValidationResult {
	[name: string]: ValidationResult;
}

const VALID: ValidationSuccessResult = { valid: true };

const invalid = (message: string): ValidationFailureResult => {
	return { valid: false, message };
};

export const EMAIL_REGEX = /^[^\s]+@[^\s]+\.[^\s]+$/;

const validateStringValue = (
	value: string,
	{ regex, maximum }: StringFieldConfiguration,
): ValidationResult => {
	if (regex && !new RegExp(`^${regex}$`).test(value)) {
		return invalid(`Must match pattern: ${regex}`);
	}
	if (maximum && value.length > +maximum) {
		return invalid(`Must be at most ${maximum} characters`);
	}
	return VALID;
};

const validateNumberValue = (
	value: string,
	{ minimum, maximum }: NumberFieldConfiguration,
): ValidationResult => {
	if (!_.isNull(value) && !_.isFinite(Number(value))) {
		return invalid('Invalid number value');
	}
	if (minimum && parseFloat(value) < parseFloat(minimum)) {
		return invalid(`Minimum is ${minimum}`);
	}
	if (maximum && parseFloat(value) > parseFloat(maximum)) {
		return invalid(`Maximum is ${maximum}`);
	}
	return VALID;
};

const getOffsetDay = (dateOffset: DateOffsetConfiguration) => {
	return getDateWithOffset(
		parseInt(dateOffset.amount, 10),
		dateOffset.field,
		dateOffset.roundToDay,
	);
};

const validateDateValue = (
	value: string,
	{
		minimum,
		maximum,
		minimumOffset,
		maximumOffset,
		precision,
	}: DateFieldConfiguration,
): ValidationResult => {
	let offSetDate: dayjs.Dayjs;
	const valDate = new Date(parseInt(value));
	const valueDate = dayjs(valDate);

	const isDateBeforeMin =
		valueDate.startOf(precision) < dayjs(minimum).startOf(precision);
	if (minimum && isDateBeforeMin) {
		return invalid('Date is before minimum');
	}
	const isDateAfterMax =
		valueDate.startOf(precision) > dayjs(maximum).startOf(precision);
	if (maximum && isDateAfterMax) {
		return invalid('Date is after maximum');
	}

	offSetDate = minimumOffset ? getOffsetDay(minimumOffset) : null;
	const isDateBeforeMinOffset = offSetDate
		? valueDate.startOf(precision) < offSetDate.startOf(precision)
		: true;

	if (minimumOffset && isDateBeforeMinOffset) {
		return invalid('Date is before minimum');
	}

	offSetDate = maximumOffset ? getOffsetDay(maximumOffset) : null;
	const isDateAfterMaxOffset = offSetDate
		? valueDate.startOf(precision) > offSetDate.startOf(precision)
		: true;
	if (maximumOffset && isDateAfterMaxOffset) {
		return invalid('Date is after maximum');
	}
	return VALID;
};

const validateEmailValue = (value: string): ValidationResult => {
	return EMAIL_REGEX.test(value) ? VALID : invalid('Invalid email address');
};

const validateSingleValue = (
	value: string,
	field: FieldConfiguration,
	fieldGroupsByLocator: FieldGroupsByLocatorMap,
): ValidationResult => {
	switch (field.type) {
		case 'string':
			return validateStringValue(value, field as StringFieldConfiguration);
		case 'number':
			return validateNumberValue(value, field as NumberFieldConfiguration);
		case 'date':
			return validateDateValue(value, field as DateFieldConfiguration);
		case 'select':
			return VALID;
		case 'email':
			return validateEmailValue(value);
		case 'media':
			return VALID;
		case 'group':
			return validateAll(
				validateValues(
					fieldGroupsByLocator[value],
					(field as GroupFieldConfiguration).fields,
					fieldGroupsByLocator,
				),
			);
		case 'lookup':
			return VALID;
		default:
			throw new Error(`Cannot validate field: ${field}`);
	}
};

const validateValueList = (
	values: FieldValues,
	field: FieldConfiguration,
	fieldGroupsByLocator: FieldGroupsByLocatorMap,
): ValidationResult => {
	const validations = values.map((v) =>
		validateSingleValue(v, field, fieldGroupsByLocator),
	);
	// If any validation failed, then the field validation will be invalid
	const invalidValidation = _.find(
		validations,
		(validation) => !validation.valid,
	);
	return invalidValidation || VALID;
};

/**
 *
 * @param valuesMap - Format: { field: value }
 * @param fields - the field configuration.
 * @param name - the field to validate
 * @param fieldGroupsByLocator
 */
const validateValue = (
	valuesMap: FieldValuesMap,
	fields: FieldConfiguration[],
	name: string,
	fieldGroupsByLocator: FieldGroupsByLocatorMap,
): ValidationResult => {
	const field = _.find(fields, (f) => f.name === name);
	if (!valuesMap) {
		valuesMap = {};
	}
	const values = valuesMap[name];
	if (isFieldAvailableForDisplay(fields, valuesMap, name)) {
		if (values && values.length > 0) {
			return validateValueList(values, field, fieldGroupsByLocator);
		} else {
			return field.optional ? VALID : invalid('Required');
		}
	} else {
		// field is not available for display and therefore is valid regardless of it's value
		return VALID;
	}
};

/**
 *
 * @param values - Format: { field: [values] }
 * Example:
 * {
 * 	10_year_felony_conviction: ["No"],
 * 	atfault_claims_past_5_years: ["None"],
 * 	channel: ["Direct"],
 * 	drivers: ["858c4402-9efc-424f-a49f-57b1c80e34d8", "cb69919d-97d5-4041-88b7-8057ea6c4447"],
 * 	insurance_fraud_convcition: ["No"],
 * 	multiple_drivers: ["Yes - Other Drivers"]
 * }
 * @param fields
 * @param fieldGroupsByLocator - Format: { locator: { field: value } }
 * Example:
 * {
 * 	858c4402-9efc-424f-a49f-57b1c80e34d8: { driver_license: ["123456789"] },
 * 	cb69919d-97d5-4041-88b7-8057ea6c4447: { driver_license: ["asdf"] }
 * }
 *
 * @returns fieldValidationResult - Format: { field: { key: val } }
 * Example:
 * {
 * 	driver_designation: { valid: true }
 * 	driver_license: { valid: false, message: "Must match pattern: ^[0-9]{9}$" }
 * 	driver_firstname: { valid: false, message: "Required" }
 * 	driver_lastname: { valid: false, message: "Invalid" }
 * }
 */
const validateValues = (
	values: FieldValuesMap,
	fields: FieldConfiguration[],
	fieldGroupsByLocator: FieldGroupsByLocatorMap,
): FieldValidationResult => {
	const retMap = _.mapValues(
		_.keyBy(fields, (field: any) => field.name),
		(field) => {
			const validationRes = validateValue(
				values,
				fields,
				field.name,
				fieldGroupsByLocator,
			);
			return validationRes;
		},
	);
	return retMap;
};

const validateList = <T>(validations: ValidationResult[]): ValidationResult => {
	return validations.every((v: ValidationResult) => v.valid)
		? VALID
		: invalid(getValidationError(validations));
};

const validateAll = <T>(validation: Validation<T>): ValidationResult => {
	const validationResults = _.values(validation) as ValidationResult[];
	return validationResults.every((v: ValidationResult) => v.valid)
		? VALID
		: invalid(getValidationError(validationResults));
};

const getValidationError = ([first, ...rest]: ValidationResult[]): string => {
	if (sameError(rest, first)) {
		return (first as ValidationFailureResult).message;
	} else {
		return 'Invalid';
	}
};

const sameError = (
	compare: ValidationResult[],
	against: ValidationResult,
): boolean => {
	return compare.every(
		(v) =>
			(v as ValidationFailureResult).message ===
			(against as ValidationFailureResult).message,
	);
};

const getDateWithOffset = (
	amount: number,
	duration: string,
	roundToDay?: boolean,
) => {
	let date = dayjs();
	date = date.add(amount, duration as ManipulateType);

	if (roundToDay) {
		date = date.startOf('day');
	}

	return date;
};

export const validatePeril = (
	peril: Peril,
	exposure: Exposure,
	config: PerilConfigurationResponse,
	policyViewTimestamp?: string,
): ValidationResult => {
	const perilCoverage = getPerilCoverage(peril, exposure, policyViewTimestamp);

	if (!perilCoverage.characteristics) {
		return invalid('Invalid');
	}

	const { fieldValues, fieldGroupsByLocator } = perilCoverage.characteristics;
	return validateAll({
		fields: validateAll(
			validateValues(fieldValues, config.fields, fieldGroupsByLocator),
		),
	});
};

const validatePerils = (
	exposure: Exposure,
	configs: PerilConfigurationResponse[],
	policyViewTimestamp?: string,
): ValidationResult => {
	if (exposure.perils.length >= 0) {
		const configsByName = _.keyBy(configs, (config) => config.name);
		const perils = exposure.perils.filter((peril) =>
			isPerilActive(peril, exposure, policyViewTimestamp),
		);
		return validateList(
			perils.map((peril) =>
				validatePeril(
					peril,
					exposure,
					configsByName[peril.name],
					policyViewTimestamp,
				),
			),
		);
	} else {
		return invalid('No perils');
	}
};

export const validateExposure = (
	exposure: Exposure,
	config: ExposureConfiguration,
	policyViewTimestamp?: string,
): ValidationResult => {
	const { fieldValues, fieldGroupsByLocator } = getExposureCharacteristics(
		exposure,
		policyViewTimestamp,
	);

	return validateAll({
		fields: validateAll(
			validateValues(fieldValues, config.fields, fieldGroupsByLocator),
		),
		perils: validatePerils(exposure, config.perils, policyViewTimestamp),
	});
};

export const validateExposures = (
	exposures: Exposure[],
	configs: ExposureConfiguration[],
	policyViewTimestamp?: string,
): ValidationResult => {
	if (exposures.length > 0) {
		const configsByName = _.keyBy(configs, (config) => config.name);
		return validateList(
			exposures.map((exposure) =>
				validateExposure(
					exposure,
					configsByName[exposure.name],
					policyViewTimestamp,
				),
			),
		);
	} else {
		return VALID; // policies do not need to have exposures
	}
};

export const validatePolicy = (
	policy: Policy2,
	config: PolicyConfiguration,
	policyViewTimestamp?: string,
): ValidationResult => {
	const { fieldValues, fieldGroupsByLocator } = getPolicyCharacteristics(
		policy,
		policyViewTimestamp,
	);
	return validateAll({
		fields: validateAll(
			validateValues(fieldValues, config.fields, fieldGroupsByLocator),
		),
		exposures: validateExposures(
			policy.exposures,
			config.exposures,
			policyViewTimestamp,
		),
	});
};

export const validatePolicyDetails = (
	policy: Policy2,
	config: PolicyConfiguration,
	policyViewTimestamp?: string,
): ValidationResult => {
	const { fieldValues, fieldGroupsByLocator } = getPolicyCharacteristics(
		policy,
		policyViewTimestamp,
	);
	return validateAll({
		fields: validateAll(
			validateValues(fieldValues, config.fields, fieldGroupsByLocator),
		),
	});
};

export const validateQuote = (
	quoteCharacteristics: PolicyCharacteristics,
	exposures: Exposure[],
	config: PolicyConfiguration,
) => {
	const { fieldValues, fieldGroupsByLocator } = quoteCharacteristics;
	return validateAll({
		fields: validateAll(
			validateValues(fieldValues, config.fields, fieldGroupsByLocator),
		),
		exposures: validateExposures(exposures, config.exposures),
	});
};

export function validatePolicyEndorsement(
	policy: Policy2,
	updatePolicy: PolicyUpdateRequest,
	config: PolicyConfiguration,
	timestamp: string,
): ValidationResult {
	const { fieldValues, fieldGroupsByLocator } = getAmendedFieldData(
		getPolicyCharacteristics(policy, timestamp),
		updatePolicy,
	);
	return validateAll({
		fields: validateAll(
			validateValues(fieldValues, config.fields, fieldGroupsByLocator),
		),
		addExposures: validateAddExposureEndorsements(
			updatePolicy.addExposures,
			config.exposures,
		),
		updateExposures: validateUpdateExposureEndorsements(
			policy.exposures,
			updatePolicy.updateExposures,
			config.exposures,
			timestamp,
		),
	});
}

export function validateUpdateExposureEndorsements(
	exposures: Exposure[],
	exposureUpdateRequests: ExposureUpdateRequest[],
	configs: ExposureConfiguration[],
	timestamp: string,
): ValidationResult {
	if (exposureUpdateRequests.length > 0) {
		const configsByName = _.keyBy(configs, (config) => config.name);
		return validateList(
			exposureUpdateRequests
				.filter((eur) => !!eur)
				.map((exposureUpdateRequest) => {
					const exposure = _.find(
						exposures,
						(e) => e.locator === exposureUpdateRequest.exposureLocator,
					);
					if (!exposure) {
						return invalid('Updated exposure does not exist');
					}
					return validateUpdateExposureEndorsement(
						exposure,
						exposureUpdateRequest,
						configsByName[exposure.name],
						timestamp,
					);
				}),
		);
	} else {
		return VALID;
	}
}

export function validateUpdateExposureEndorsement(
	exposure: Exposure,
	exposureUpdateRequest: ExposureUpdateRequest,
	config: ExposureConfiguration,
	timestamp: string,
): ValidationResult {
	const { fieldValues, fieldGroupsByLocator } = getAmendedFieldData(
		getExposureCharacteristics(exposure, timestamp),
		exposureUpdateRequest,
	);
	return validateAll({
		fields: validateAll(
			validateValues(fieldValues, config.fields, fieldGroupsByLocator),
		),
	});
}

export function validateAddExposureEndorsements(
	exposureCreateRequests: ExposureCreateRequest[],
	configs: ExposureConfiguration[],
): ValidationResult {
	if (exposureCreateRequests.length > 0) {
		const configsByName = _.keyBy(configs, (config) => config.name);
		return validateList(
			exposureCreateRequests
				.filter((ecr) => !!ecr)
				.map((exposureCreateRequest) => {
					return validateAddExposureEndorsement(
						exposureCreateRequest,
						configsByName[exposureCreateRequest.exposureName],
					);
				}),
		);
	} else {
		return VALID;
	}
}

export function validateAddExposureEndorsement(
	exposureCreateRequest: ExposureCreateRequest,
	config: ExposureConfiguration,
): ValidationResult {
	const { fieldValues, fieldGroupsByLocator } =
		getCharacteristicsFromCreateRequest(exposureCreateRequest);
	return validateAll({
		fields: validateAll(
			validateValues(fieldValues, config.fields, fieldGroupsByLocator),
		),
		perils: validateAddPerilEndorsements(
			exposureCreateRequest.perils,
			config.perils,
		),
	});
}

function validateAddPerilEndorsements(
	perilCreateRequests: PerilCreateRequest[],
	configs: PerilConfigurationResponse[],
): ValidationResult {
	const configsByName = _.keyBy(configs, (config) => config.name);
	return validateList(
		perilCreateRequests
			.filter((pcr) => !!pcr)
			.map((perilCreateRequest) => {
				return validateAddPerilEndorsement(
					perilCreateRequest,
					configsByName[perilCreateRequest.name],
				);
			}),
	);
}

export function validateAddPerilEndorsement(
	perilCreateRequest: PerilCreateRequest,
	config: PerilConfigurationResponse,
): ValidationResult {
	const { fieldValues, fieldGroupsByLocator } =
		getCharacteristicsFromCreateRequest(perilCreateRequest);
	return validateAll({
		fields: validateAll(
			validateValues(fieldValues, config.fields, fieldGroupsByLocator),
		),
	});
}
